import type { CalendarEntry, CalendarFilter, ChoreoLink } from './calendar'
import { bookingIsOff } from './privateLessons'

export type BookingKind = 'PrivateLesson' | 'Choreography' | 'Rehearsal' | 'Other'
export type BookingStatus = 'Reserved' | 'Booked' | 'Cancelled'

export type BookedSlot = {
  id: string
  date: string
  startTime: string
  endTime: string
  roomId: string
  roomName: string
  roomColor: string | null
  staffId: string
  staffName: string
  kind: BookingKind
  status: BookingStatus
  /** Solo / Duo / Trio / Open — derived from the roster, never stored. */
  sizeLabel: string
  dancerNames: string[]
  dancerIds: string[]
  /** What the family reads on her schedule (D-656). */
  notes: string | null
  /**
   * The studio own note (D-656).
   *
   * This shape is staff-only: the parent portal builds its own row and never
   * receives it. If that ever stops being true, this is the field to check.
   */
  studioNotes?: string | null
  isOpen: boolean
  /**
   * A private lesson's sessions, one per dancer in `dancerNames` order (D-733).
   * Attendance lives on the session, so this is how the one card for a private
   * opens it. Empty for a held room, a rehearsal, or a private with no makeup.
   */
  sessionIds?: string[]
  /**
   * A private whose every session is cancelled while the booking stands - what a
   * closure leaves behind (D-407). Read through `bookingIsOff`, never on its own.
   */
  lessonCalledOff?: boolean
}

export type ConflictVerdict = 'Expected' | 'NeedsFixing'

/**
 * One side of a clash, in enough detail to open it (D-567).
 *
 * `id` is the dated thing — an occurrence, or a booking — and `sourceId` is the
 * arrangement behind it (A-1). For a booking they are equal; for a class they
 * are not, and which one a screen wants depends on whether it is asking about
 * this Tuesday or about the class.
 */
export type ConflictSide = {
  id: string
  sourceId: string
  kind: 'Class' | 'Booking' | 'Hold'
  label: string
  start: string
  end: string
  roomId: string | null
  roomName: string | null
}

/** One thing a clashing pair has in common. */
export type ConflictCollision = {
  kind: 'Teacher' | 'Room' | 'Dancer'
  subject: string
  subjectId: string
  /**
   * Whether this dancer's family has anyone who could answer (D-570).
   *
   * Per collision rather than per row, because one row can name two children
   * and only one of their households may have a login.
   */
  canBeAsked?: boolean
}

export type Conflict = {
  /** Identifies the pairing across every week it recurs (D-150). */
  key: string
  /**
   * The collision the row leads with - a dancer where there is one (D-609).
   *
   * `collides` carries the whole truth; these two stay because a row still has
   * to say *something* in one line, and because every caller that only wants a
   * headline reads them.
   */
  kind: 'Teacher' | 'Room' | 'Dancer'
  subject: string
  /** The dancer, teacher or room the two have in common — `kind` says which. */
  subjectId: string
  date: string
  overlap: string
  first: string
  second: string
  firstSide: ConflictSide
  secondSide: ConflictSide
  description: string
  /** The studio's verdict, where one has been given. */
  verdict?: ConflictVerdict | null
  note?: string | null
  /**
   * Everything these two classes collide on, on one row (D-609).
   *
   * The same pair used to arrive up to three times - once under the teacher,
   * once under the room, once per dancer - which read as three problems to fix
   * and was one.
   */
  collides?: ConflictCollision[] | null
}

/**
 * What an answer is *about*, in the form the server stores (D-609).
 *
 * An answer covers what was asked and no more: accepting a teacher clash must
 * not quietly excuse those same two classes landing in one room next term. Sent
 * on every review so the server can tell the difference.
 */
export function collisionTokens(conflict: Conflict): string[] {
  const all = conflict.collides?.length
    ? conflict.collides
    : [{ kind: conflict.kind, subjectId: conflict.subjectId }]

  return all.map((c) => `${c.kind}:${c.subjectId}`)
}

function range(from: string, to: string) {
  return `from=${from}&to=${to}`
}

/** A room an approved weekend hold reserves on one date (D-710). */
export type HeldRoom = {
  date: string
  roomId: string
  startTime: string
  endTime: string
  heldForStaffId: string
  /** "Held - Hannah Jones". */
  label: string
}

/** Approved weekend holds in a range; they are not bookings, so the list above never has them. */
export async function fetchHolds(from: string, to: string): Promise<HeldRoom[]> {
  const res = await fetch(`/api/bookings/holds?${range(from, to)}`)
  if (!res.ok) throw new Error(`Holds request failed (${res.status})`)
  return res.json()
}

export async function fetchBookings(from: string, to: string): Promise<BookedSlot[]> {
  const res = await fetch(`/api/bookings?${range(from, to)}`)
  if (!res.ok) throw new Error(`Bookings request failed (${res.status})`)
  return res.json()
}

/**
 * The length past which a booking waits on Tonya (D-396).
 *
 * Read rather than hardcoded: it is `booking.maxDurationMinutes`, a setting
 * she can move, and a form asserting 90 while the server enforces 60 would be
 * confidently wrong in the one place a person is deciding what to pick.
 */
export async function fetchBookingLimits(): Promise<{ maxDurationMinutes: number }> {
  const res = await fetch('/api/bookings/limits')
  if (!res.ok) throw new Error(`Booking limits request failed (${res.status})`)
  return res.json()
}

export async function fetchOpenSlots(from: string, to: string): Promise<BookedSlot[]> {
  const res = await fetch(`/api/bookings/open?${range(from, to)}`)
  if (!res.ok) throw new Error(`Open slots request failed (${res.status})`)
  return res.json()
}

/**
 * A booking held over the auto-confirm limit, waiting on Tonya (D-163).
 *
 * The room is held meanwhile — refusing to hold it would let a second teacher
 * take the slot out from under the first, which is the problem this replaces.
 */
/**
 * How long a booking ran, in the studio's own words.
 *
 * "1 hour 30 minutes" rather than "90 minutes", because the number Tonya is
 * judging against is a length of lesson, not a count of minutes.
 */
export function durationLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60

  if (hours === 0) return `${rest} minutes`
  if (rest === 0) return hours === 1 ? '1 hour' : `${hours} hours`

  return `${hours === 1 ? '1 hour' : `${hours} hours`} ${rest} minutes`
}

export async function fetchConflicts(from: string, to: string): Promise<Conflict[]> {
  const res = await fetch(`/api/bookings/conflicts?${range(from, to)}`)
  if (!res.ok) throw new Error(`Conflict check failed (${res.status})`)
  return res.json()
}

/**
 * Records what the studio decided about a clash (D-150).
 *
 * Keyed on the pairing rather than the date, so one answer covers every week
 * the two things overlap.
 */
export async function reviewConflict(input: {
  key: string
  verdict: ConflictVerdict
  note?: string
  summary?: string
  /** What the pair collided on when she answered (D-609). */
  collides?: string[]
}): Promise<void> {
  const res = await fetch('/api/bookings/conflicts/review', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) throw new Error(`Could not save that (${res.status})`)
}

/** Undoes a verdict, returning the clash to the list. */
export async function clearConflictReview(key: string): Promise<void> {
  const res = await fetch(`/api/bookings/conflicts/review?key=${encodeURIComponent(key)}`, {
    method: 'DELETE',
  })
  if (!res.ok) throw new Error(`Could not undo that (${res.status})`)
}

/**
 * One row of a teacher's day, whatever kind of thing it is.
 *
 * The teacher grid has to show classes and privates in one lane — a teacher
 * cannot be in two places at once regardless of which sort of commitment each
 * one is, and splitting them into separate rows would hide exactly the clash
 * the grid exists to reveal.
 */
export type TeacherLane = {
  staffId: string
  staffName: string
  items: TeacherItem[]
}

export type TeacherItem = {
  /** Unique per lane row; composite for a class, which appears in each teacher's lane. */
  id: string
  /** The occurrence or booking id, for fetching the roster. */
  sourceId: string
  label: string
  startTime: string
  endTime: string
  roomName: string | null
  roomColor: string | null
  kind: 'class' | 'booking'
  isOpen: boolean
  /**
   * What a verdict is given about — the class, or the booking series (D-150).
   *
   * Distinct from {@link sourceId}, which identifies one dated session. Two
   * classes clashing every week are one arrangement, judged once.
   */
  arrangementId: string
  /** True when this item overlaps another the studio has not said is fine. */
  clashes: boolean
}

/** "17:15:00" -> minutes since midnight. */
export function minutesOf(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

/**
 * The key a clash between two arrangements is judged under (D-150).
 *
 * Mirrors `ScheduleConflict.KeyFor` on the server: the two sources in a fixed
 * order, so it does not depend on which was compared first.
 *
 * <b>The kind and the subject came out of it with D-609.</b> One pair of
 * classes is one question however many ways it collides, so the key that
 * quiets this grid is the key the conflicts screen answered - and it has to be
 * *the same string*, because the whole point is that a verdict given there
 * stops the red here. Two spellings of one key is the failure mode this
 * project keeps recording (A-17), and it fails silently: the grid simply never
 * matches, and Tonya's answer appears to change nothing.
 */
export function conflictKeyFor(a: string, b: string): string {
  const [low, high] = a <= b ? [a, b] : [b, a]
  return `${low}|${high}`
}

/**
 * Marks every item that overlaps another the studio has not already accepted.
 *
 * Half-open comparison, matching the server: back-to-back sessions are how the
 * studio runs, so touching endpoints must not read as a clash.
 *
 * <b>`accepted` is what keeps this screen honest.</b> A verdict given on the
 * conflicts screen has to quiet the teacher grid too — otherwise Tonya marks an
 * arrangement as deliberate and it keeps being reported in red somewhere else,
 * which teaches her that answering the question changes nothing.
 */
export function markClashes(
  items: TeacherItem[],
  staffId?: string,
  accepted?: ReadonlySet<string>,
): TeacherItem[] {
  const marked = items.map((i) => ({ ...i, clashes: false }))

  for (let i = 0; i < marked.length; i++) {
    for (let j = i + 1; j < marked.length; j++) {
      const a = marked[i]
      const b = marked[j]
      const overlaps =
        minutesOf(a.startTime) < minutesOf(b.endTime) &&
        minutesOf(b.startTime) < minutesOf(a.endTime)

      if (!overlaps) continue

      // The pair, not the teacher (D-609). `staffId` is still taken so the
      // caller says whose lane this is, and so a future key can use it again.
      if (staffId && accepted?.has(conflictKeyFor(a.arrangementId, b.arrangementId))) {
        continue
      }

      a.clashes = true
      b.clashes = true
    }
  }

  return marked
}

/**
 * Splits overlapping items in one column into side-by-side sub-columns.
 *
 * In a room column an overlap is a rare mistake; in a teacher column it is the
 * thing the view exists to show, so the two blocks cannot simply be drawn on
 * top of each other. Items are grouped into clusters of mutual overlap and each
 * cluster is divided evenly, the way a calendar app lays out competing events.
 *
 * Returns the same items with `column` (0-based) and `columnCount` for the
 * cluster they belong to; a non-overlapping item is always 0 of 1.
 */
export function assignColumns<T extends { startTime: string; endTime: string }>(
  items: T[],
): (T & { column: number; columnCount: number })[] {
  const sorted = [...items].sort(
    (a, b) => minutesOf(a.startTime) - minutesOf(b.startTime) || minutesOf(a.endTime) - minutesOf(b.endTime),
  )

  const out: (T & { column: number; columnCount: number })[] = []
  let cluster: (T & { column: number; columnCount: number })[] = []
  let clusterEnd = -1

  const closeCluster = () => {
    for (const item of cluster) item.columnCount = cluster.length
    out.push(...cluster)
    cluster = []
    clusterEnd = -1
  }

  for (const item of sorted) {
    const start = minutesOf(item.startTime)

    // A cluster ends the moment an item starts at or after everything in it has
    // finished — half-open, so back-to-back items start a fresh cluster and get
    // the full width rather than being squeezed by their neighbour.
    if (cluster.length > 0 && start >= clusterEnd) closeCluster()

    // First free sub-column: reuse one whose occupant has already finished.
    const taken = new Set(
      cluster.filter((c) => minutesOf(c.endTime) > start).map((c) => c.column),
    )
    let column = 0
    while (taken.has(column)) column++

    cluster.push({ ...item, column, columnCount: 1 })
    clusterEnd = Math.max(clusterEnd, minutesOf(item.endTime))
  }

  if (cluster.length > 0) closeCluster()
  return out
}

export type TeacherLaneFilter = {
  /** Substring match on the teacher's name. */
  q?: string
  /** Show only teachers who are in two places at once. */
  onlyConflicts?: boolean
  /** Explicit selection; empty means "no restriction". */
  staffIds?: string[]
}

/**
 * Narrows the teacher grid.
 *
 * The studio has around seventeen teachers, and seventeen readable columns is
 * roughly 2,200px — wider than most screens. Filtering is what keeps this view
 * usable rather than a horizontal scroll to nowhere.
 *
 * `onlyConflicts` is the important one: the grid exists to answer "is anybody
 * double-booked today", and that answer should not depend on how many people
 * happen to be teaching.
 */
export function filterLanes(lanes: TeacherLane[], filter: TeacherLaneFilter): TeacherLane[] {
  const q = filter.q?.trim().toLowerCase()

  return lanes.filter((lane) => {
    if (filter.onlyConflicts && !lane.items.some((i) => i.clashes)) return false
    if (filter.staffIds?.length && !filter.staffIds.includes(lane.staffId)) return false
    if (q && !lane.staffName.toLowerCase().includes(q)) return false
    return true
  })
}

export function laneFilterIsActive(filter: TeacherLaneFilter): boolean {
  return Boolean(filter.q?.trim() || filter.onlyConflicts || filter.staffIds?.length)
}

/** One thing occupying a room for part of a day — a class or a booking. */
export type RoomItem = {
  id: string
  sourceId: string
  label: string
  detail: string | null
  startTime: string
  endTime: string
  kind: 'class' | 'booking'
  isOpen: boolean
  isCancelled: boolean

  /**
   * Who is on this class, as ids (D-539). Empty for a booking.
   *
   * `detail` already carries the *names*, joined for display; ids are what
   * `canOpenRoster` needs to decide whether the block may offer a roster at
   * all, and names are never keys here (D-82).
   */
  instructorIds: string[]
}

/**
 * Everything in one room on one day, classes and bookings together.
 *
 * Merged rather than layered, because the room grid answers "is this room in
 * use at 7pm?" and a view that shows only classes answers it wrongly for
 * roughly half the studio's week (D-78).
 */
export function buildRoomItems(
  entries: Pick<CalendarEntry, 'occurrenceId' | 'className' | 'startTime' | 'endTime' | 'instructors' | 'isCancelled'>[],
  bookings: BookedSlot[],
): RoomItem[] {
  const items: RoomItem[] = []

  for (const e of entries) {
    if (!e.startTime || !e.endTime) continue
    items.push({
      id: e.occurrenceId,
      sourceId: e.occurrenceId,
      label: e.className,
      detail: e.instructors.map((i) => i.name).join(', ') || null,
      instructorIds: e.instructors.map((i) => i.id),
      startTime: e.startTime,
      endTime: e.endTime,
      kind: 'class',
      isOpen: false,
      isCancelled: e.isCancelled,
    })
  }

  for (const b of bookings) {
    if (bookingIsOff(b)) continue
    items.push({
      id: b.id,
      sourceId: b.id,
      // An unfilled slot is named by who is holding it, because that is the
      // person to ask about taking it.
      label: b.isOpen ? `Open - ${b.staffName}` : b.dancerNames.join(', ') || b.sizeLabel,
      detail: b.isOpen ? null : `${b.sizeLabel} - ${b.staffName}`,
      startTime: b.startTime,
      endTime: b.endTime,
      kind: 'booking',
      isOpen: b.isOpen,
      isCancelled: false,
      // A booking has no roster to offer, so nobody is "on" it (D-80).
      instructorIds: [],
    })
  }

  return items
}

/** Groups a day's classes and bookings into one lane per teacher. */
export function buildLanes(
  bookings: BookedSlot[],
  classes: Pick<
    CalendarEntry,
    'occurrenceId' | 'classId' | 'className' | 'startTime' | 'endTime' | 'room' | 'roomColor' | 'instructors'
  >[],
  /** Teacher conflict keys the studio has marked as expected (D-150). */
  accepted?: ReadonlySet<string>,
): TeacherLane[] {
  const lanes = new Map<string, TeacherLane>()

  const laneFor = (id: string, name: string) => {
    if (!lanes.has(id)) lanes.set(id, { staffId: id, staffName: name, items: [] })
    return lanes.get(id)!
  }

  for (const b of bookings) {
    if (bookingIsOff(b)) continue
    laneFor(b.staffId, b.staffName).items.push({
      id: b.id,
      sourceId: b.id,
      // A one-off booking is its own arrangement. A recurring series would
      // key on the series, which the day payload does not carry — so a
      // weekly private is judged per week until it does.
      arrangementId: b.id,
      label: b.isOpen ? `Open - ${b.sizeLabel}` : b.dancerNames.join(', ') || b.sizeLabel,
      startTime: b.startTime,
      endTime: b.endTime,
      roomName: b.roomName,
      roomColor: b.roomColor,
      kind: 'booking',
      isOpen: b.isOpen,
      clashes: false,
    })
  }

  /*
   * Keyed on the instructor id, never the name. The studio has two Ashleys —
   * "ASH A" and "ASH D" in the workbook — and grouping by name would collapse
   * them into a single lane, hiding a real double-booking behind what looks
   * like one busy teacher (D-82).
   */
  for (const c of classes) {
    if (!c.startTime || !c.endTime) continue
    for (const instructor of c.instructors) {
      laneFor(instructor.id, instructor.name).items.push({
        // Composite, because a co-taught class appears once per teacher's lane
        // and React keys must stay unique across the grid.
        id: `${c.occurrenceId}-${instructor.id}`,
        sourceId: c.occurrenceId,
        // The class, not this Tuesday's session: a verdict has to hold for
        // every week the two overlap (D-150).
        arrangementId: c.classId,
        label: c.className,
        startTime: c.startTime,
        endTime: c.endTime,
        roomName: c.room,
        roomColor: c.roomColor,
        kind: 'class',
        isOpen: false,
        clashes: false,
      })
    }
  }

  return [...lanes.values()]
    .map((lane) => ({
      ...lane,
      items: markClashes(lane.items, lane.staffId, accepted).sort(
        (a, b) => minutesOf(a.startTime) - minutesOf(b.startTime),
      ),
    }))
    .sort((a, b) => a.staffName.localeCompare(b.staffName))
}

export type RosterDancer = {
  dancerId: string
  name: string
  /** Her age on the session's own date. Null when her household has gone (D-478). */
  ageYears: number | null
  household: string | null
  /**
   * The session she is here to make up — a visitor only, null for everybody
   * else (D-480).
   *
   * So a teacher knows why an unfamiliar dancer is in her room. The kiosk
   * already puts the same sentence under the name.
   */
  makesUpFor?: string | null
  /**
   * Her family has said she will not be there.
   *
   * **The fact only.** The reason, the reporter, the parent's note and any
   * document stay on the attendance sheet behind `TakeAttendance`; this
   * payload is `ViewPeople` (D-9, D-233). And it does not mean excused —
   * telling the studio is courtesy, not an exemption (D-188).
   */
  reportedOut?: boolean
}

export type Roster = {
  kind: 'class' | 'booking'
  /** Null for a booking, which holds a room rather than a class (D-80). */
  studioClassId: string | null
  title: string
  subtitle: string | null
  date: string
  startTime: string | null
  endTime: string | null
  /**
   * Where it meets, as an id (D-893).
   *
   * **For matching, never for showing** — `roomName` is the one that renders.
   * The move panel needs to know which of the studio's rooms is the one this
   * night is already in, and answering that from a name is D-82's mistake one
   * noun over. Optional so a client newer than the server reads as *no room
   * known* rather than crashing, which is the ~75 seconds after a deploy
   * (D-33); the panel then offers the rooms without marking one as current.
   */
  roomId?: string | null
  roomName: string | null
  roomColor: string | null
  instructors: string[]
  /**
   * Who is teaching it, as ids (D-898).
   *
   * **For asking about their time, never for showing** — `instructors` is what
   * a register prints, with its *(sub)* suffix. Optional so a client newer than
   * the server reads as *nobody known* rather than crashing in the ~75 seconds
   * after a deploy (D-33); the move panel then offers no availability grid,
   * which is the safe direction.
   */
  teachingStaffIds?: string[]
  capacity: number | null
  /**
   * The dated session this is about, when it is about one (D-477).
   *
   * Null from the class door and for a booking. It is what lets a screen ask
   * "which session, and has it begun" without inspecting which door it came
   * through — the distinction D-466 removed from everything else.
   */
  occurrenceId?: string | null
  /** The booking a private lesson belongs to, so its panel can offer to change it (D-733). */
  bookingId?: string | null
  /** Each dancer's session on that booking, to switch between on a duet (D-733). */
  lessonSessions?: { occurrenceId: string; dancerName: string }[] | null
  /**
   * Somebody is standing in on this night (D-492).
   *
   * A flag rather than a suffix to parse: `instructors` already reads
   * "Hannah Jones (sub)", and deciding whether to offer the correction by
   * looking for that would be reading a label written for a human. Optional
   * so an older cached payload reads as "nobody is covering" rather than
   * crashing.
   */
  hasSubstitute?: boolean
  /** Enrolled in this class. Never the visitors — see below (D-480). */
  dancers: RosterDancer[]
  /**
   * Dropping in from another class to make up a session she missed (D-14).
   *
   * **Its own list, because a teacher needs to know which dancers are hers**
   * (PRD §7.6, D-480) — the same separation the attendance sheet and the
   * kiosk already keep. Optional so an older cached payload reads as no
   * visitors rather than as a crash; always present from the occurrence door,
   * always empty from the other two.
   */
  makeupVisitors?: RosterDancer[]
  /** The routine and its music. Only the class view carries them (D-319). */
  routineName?: string | null
  musicUrl?: string | null
  /** The practice videos for families (D-541), in order; the editor behind the class view shows and changes them. */
  choreoLinks?: ChoreoLink[]
  /**
   * This reader may move this night to another day or hour (D-698, D-701).
   *
   * **The server's answer, because the client cannot work it out.** Holding
   * `MoveOwnSessions` says what *kind* of thing she may do and never which
   * classes (D-286) — and every staff member may open any class's roster from
   * the studio schedule (D-299), so a control keyed on the capability alone sat
   * on every class in the building and was refused with a bare 404.
   *
   * Optional so a missing field reads as "not hers", which is the safe
   * direction: a control that is missing costs a trip to the studio, and one
   * that refuses its holder costs her trust in every other control on the page.
   *
   * **And the case that produces a missing field is a client newer than the
   * server** — not a stale cache, which an audit corrected here. The server
   * sends a non-nullable `bool`, and react-query's cache is in memory and dies
   * on reload, so a response from this build always carries it. What does not
   * is the ~75-second window after a deploy where the SPA has landed and the
   * API has not (D-33), and a browser holding the new bundle against a rolled
   * back API.
   */
  mayMove?: boolean

  /**
   * Whether this reader may call this night off (D-888, D-890).
   *
   * **Optional for the same reason `mayMove` is**, and the same window: a
   * client newer than the server, which is the ~75 seconds after a deploy when
   * the SPA has landed and the API has not (D-33). Absent reads as *no*, so the
   * control is simply not offered — never offered-and-refused.
   *
   * **Not the same answer as `mayMove`**, deliberately: a substitute may call
   * off the night she is covering and may not move it (D-887). The two sit in
   * one row of buttons, so the asymmetry is visible rather than theoretical.
   */
  mayCallOff?: boolean
}

/**
 * Who is in a session.
 *
 * Fetched on demand rather than folded into the calendar payload: a week of
 * rosters is every dancer in the studio several times over, and it would be
 * sent on every calendar load to serve a panel that is opened occasionally.
 */
export async function fetchRoster(
  kind: 'class' | 'booking' | 'classList',
  id: string,
): Promise<Roster> {
  /*
   * Three kinds, and the difference between the first and the third is the
   * one that produced a 404 in live testing (D-316).
   *
   * `class` is one evening and takes a ClassOccurrence id. `classList` is the
   * class itself and takes a StudioClass id - what "My classes" has, and what
   * it was passing to the occurrence route.
   */
  const path = kind === 'booking' ? 'booking' : kind === 'classList' ? 'class' : 'occurrence'
  const res = await fetch(`/api/rosters/${path}/${id}`)

  if (res.status === 403) throw new Error('You do not have permission to view class rosters.')
  if (!res.ok) throw new Error(`Roster request failed (${res.status})`)
  return res.json()
}

/**
 * Widens a day's grid so nothing renders off it.
 *
 * The server sizes the grid from classes, which sit in the evening. Privates do
 * not (D-79) — a 9am summer lesson would land at a negative row and simply not
 * appear, which is worse than an ugly grid because nothing signals it is
 * missing. Rounded out to whole hours so the axis stays readable.
 */
export function stretchGrid(
  gridStart: number,
  gridEnd: number,
  bookings: { startTime: string; endTime: string }[],
): { start: number; end: number } {
  let start = gridStart
  let end = gridEnd

  for (const b of bookings) {
    start = Math.min(start, Math.floor(minutesOf(b.startTime) / 60) * 60)
    end = Math.max(end, Math.ceil(minutesOf(b.endTime) / 60) * 60)
  }

  return { start, end }
}

export type StaffOption = {
  staffId: string
  name: string
  title: string | null
}

/**
 * Which teacher a new booking should start on (D-373, corrected against
 * production).
 *
 * **Two conditions, and the second was found by testing the live site.** She
 * must have days here *and* not be the person who books on everybody's behalf.
 *
 * `teaches` alone is not enough. Tonya's owner account is created with
 * `UserAccount.ForStaff`, so she holds a `staffId` — and `observed` **on
 * production**, `teaches` is **true** for her: `ownsClasses` is false, but she
 * has covered a session inside the lookback window, which is all `teaches`
 * asks (D-322). In the dev database she teaches nothing, so the guard looked
 * right and was not. The field would have pre-filled *her* name, and a room
 * she booked for Ashley would have been attributed to her — then hidden from
 * Ashley's own week by D-373's scoping.
 *
 * `ViewAllSchedules` is the signal for *this reader's view is the studio, not
 * herself*: the same capability that sends her to the timetable rather than a
 * portal (`landingFor`) and hides the "show studio schedule" toggle from her
 * because she is already looking at it (D-300). A senior teacher granted it
 * loses the convenience, which is the cheaper mistake.
 *
 * **Pure, because the rendered version could not be tested honestly.** The
 * session is a query, and every assertion about a *default* resolves before it
 * settles — the negative case has no session-derived text on this screen to
 * wait for, so a rendered test of it passes at `t=0` whatever the code does.
 */
export function defaultTeacher(
  session:
    | { teaches?: boolean; staffId?: string | null; capabilities?: string[] }
    | null
    | undefined,
): string {
  if (!session?.teaches) return ''
  if (session.capabilities?.includes('ViewAllSchedules')) return ''

  return session.staffId ?? ''
}

export async function fetchStaff(): Promise<StaffOption[]> {
  const res = await fetch('/api/staff')
  if (!res.ok) throw new Error(`Staff request failed (${res.status})`)
  return res.json()
}

export type NewBooking = {
  roomId: string
  staffId: string
  date: string
  startTime: string
  endTime: string
  kind: BookingKind
  dancerIds?: string[]
  notes?: string | null
  studioNotes?: string | null
  cadence: 'Once' | 'Weekly' | 'Biweekly'
  repeatUntil?: string | null
  allowConflicts?: boolean
  /**
   * The absence this private repairs, when it is a makeup (D-406).
   *
   * The teacher and the family often settle a private by text (D-287), so she
   * books the room herself and the absence would otherwise stay unrepaired —
   * the parent's own ask is the only path that ever said what a private was
   * for. The server refuses this on anything but a one-off private lesson with
   * exactly one dancer, because a rehearsal that repairs an absence reopens
   * the group path D-405 closed.
   */
  repairsOccurrenceId?: string | null
  /**
   * The competitive technique class this lesson stands in for (D-729).
   *
   * With an absence named, that absence must be a session of this class. With
   * none, the lesson is banked: once she is marked present it covers her oldest
   * missed class of this style, now or later. A technique makeup is never left
   * to the family, so `isMakeup` is not sent with it.
   */
  techniqueClassId?: string | null

  /**
   * It is a makeup, but she is not saying which absence (D-422).
   *
   * She arranged the lesson so she knows *that* it is one; the entitlement is
   * the family's and so is the choice of what to spend it on. Ticking this
   * raises a claim that waits in the parent portal and repairs nothing until
   * they answer. Ignored when `repairsOccurrenceId` is set — naming the absence
   * is the stronger statement.
   */
  isMakeup?: boolean
}

/** One absence a private could repair, for the booking form (D-406). */
export type RepairableAbsence = {
  occurrenceId: string
  className: string
  date: string
}

/**
 * What this dancer has missed that a private would mend.
 *
 * Empty is the ordinary answer - most dancers are in no solo and have missed
 * no routine - so the field this feeds hides rather than showing an empty
 * select somebody has to reason about.
 */
export async function fetchRepairableAbsences(
  dancerId: string,
  techniqueClassId?: string | null,
): Promise<RepairableAbsence[]> {
  // Filtered to that style when it is a technique makeup (D-729).
  const query = techniqueClassId ? `?techniqueClassId=${techniqueClassId}` : ''
  const res = await fetch(`/api/bookings/repairable/${dancerId}${query}`)
  if (!res.ok) throw new Error('Could not check what a private could make up.')
  const body = await res.json()
  return Array.isArray(body) ? body : []
}

/** A competitive technique class of hers on the day of a lesson (D-729). */
export type TechniqueClass = {
  classId: string
  className: string
  style: string
  level: string | null
}

/**
 * Her technique classes on that day - what a private could stand in for.
 *
 * Empty for most dancers, and then the form never asks what kind of makeup
 * this is.
 */
export async function fetchTechniqueClasses(dancerId: string, date: string): Promise<TechniqueClass[]> {
  const res = await fetch(`/api/bookings/technique-classes/${dancerId}?date=${date}`)
  if (!res.ok) throw new Error('Could not check her technique classes.')
  const body = await res.json()
  return Array.isArray(body) ? body : []
}

export type BookingRefusal = {
  reason: 'conflict' | 'hours' | 'other'
  conflicts: Conflict[]
  message: string
}

/**
 * What is actually in the way, said in one line (D-376, completing D-368).
 *
 * **The refusal named the person and never the *kind* of clash.** `Describe()`
 * renders `"Ashley Alley: … overlap 6:00-6:30 on Wed 10 Sep"`, so a teacher
 * reading it could not tell whether the room was taken or she was. Over a room
 * the grid had just called free, that produced the owner's report — *"it shows
 * rooms are free but then showing this error, looking on the schedule i don't
 * think there are anything actually booked."* She was right: nothing was
 * booked; **she** was teaching.
 *
 * Now that the grid answers the room question honestly (D-368), a room clash
 * should be rare and a *teacher* clash is the one she will meet — so naming it
 * is what makes the remaining case make sense rather than look like a bug.
 */
export function conflictHeadline(refusal: BookingRefusal): string {
  const kinds = new Set(refusal.conflicts.map((c) => c.kind))

  // Mixed, or none reported: the generic sentence is the honest one.
  if (kinds.size !== 1) return refusal.message

  const [kind] = [...kinds]
  const who = refusal.conflicts[0].subject

  if (kind === 'Room') return `That room is already taken then.`
  if (kind === 'Teacher') return `${who} is already teaching then.`

  return `${who} is already in a class then.`
}

/**
 * Creates a booking, or explains why it was refused.
 *
 * A refusal is data, not an exception: the caller has to show Tonya what the
 * slot collided with so she can decide, and a bare "conflict" would send her
 * back to the grid to work out what she hit — the manual step this replaces.
 */
export async function createBooking(
  booking: NewBooking,
): Promise<
  | {
      ok: true
      ids: string[]
      awaitingApproval: boolean
      awaitingClassTeacher: boolean
      /** A lesson a teacher arranged, waiting on the family to agree (D-660). */
      awaitingFamily: boolean
    }
  | { ok: false; refusal: BookingRefusal }
> {
  const res = await fetch('/api/bookings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(booking),
  })

  if (res.ok) {
    const body = await res.json()

    /*
     * **`awaitingApproval` was being thrown away here** (D-376). The endpoint
     * has always sent it — its own comment says a teacher who books ninety
     * minutes should leave *"knowing the room is held but not yet settled,
     * rather than finding out when Tonya asks about it"* (D-163) — and the
     * client read `bookingIds` and dropped the rest, so she left knowing
     * nothing. Reported by the owner as wanting something that says it is
     * pending approval.
     *
     * **And it happened again, in this function, to the second gate** (D-509).
     * The endpoint sends `awaitingClassTeacherConsent` with a comment saying
     * she leaves knowing the lesson is not settled until the routine's teacher
     * answers; this read `bookingIds` and `awaitingApproval` and dropped it,
     * eight lines below the paragraph describing that exact mistake. So the
     * dialog closed silently and she left believing it was booked — and then
     * received a "Makeup agreed" email about a question she never knew had
     * been asked. Found by a cold audit.
     */
    return {
      ok: true,
      ids: body.bookingIds ?? [],
      awaitingApproval: body.awaitingApproval === true,
      awaitingClassTeacher: body.awaitingClassTeacherConsent === true,
      awaitingFamily: body.awaitingFamily === true,
    }
  }

  if (res.status === 409) {
    const body = await res.json().catch(() => ({}))
    return {
      ok: false,
      refusal: {
        reason: 'conflict',
        conflicts: body.conflicts ?? [],
        message: 'That slot collides with something already scheduled.',
      },
    }
  }

  const problem = await res.json().catch(() => null)
  return {
    ok: false,
    refusal: {
      reason: res.status === 400 ? 'other' : 'other',
      conflicts: [],
      message: problem?.detail ?? problem?.title ?? `Could not save the booking (${res.status}).`,
    },
  }
}

export async function updateBooking(
  id: string,
  booking: Omit<NewBooking, 'cadence' | 'repeatUntil'>,
): Promise<
  { ok: true; awaitingApproval: boolean } | { ok: false; refusal: BookingRefusal }
> {
  const res = await fetch(`/api/bookings/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(booking),
  })

  if (res.ok) {
    /*
     * The same field the create path reads (D-376), because the edit path can
     * now produce it (D-396). Stretching a booking past the limit sends it to
     * Tonya exactly as making one that long does, and the form has to say so —
     * it was already saying so, against a server that did nothing.
     */
    const body = await res.json().catch(() => ({}))
    return { ok: true, awaitingApproval: body.awaitingApproval === true }
  }

  const body = await res.json().catch(() => ({}))

  if (res.status === 409) {
    return {
      ok: false,
      refusal: {
        reason: 'conflict',
        conflicts: body.conflicts ?? [],
        message: 'That change collides with something already scheduled.',
      },
    }
  }

  return {
    ok: false,
    refusal: {
      reason: 'other',
      conflicts: [],
      message: body?.detail ?? body?.title ?? `Could not save the change (${res.status}).`,
    },
  }
}

export async function cancelBooking(id: string, reason?: string): Promise<void> {
  const query = reason ? `?reason=${encodeURIComponent(reason)}` : ''
  const res = await fetch(`/api/bookings/${id}/cancel${query}`, { method: 'POST' })
  if (!res.ok) throw new Error(`Could not cancel the booking (${res.status}).`)
}

/** "17:15:00" from minutes since midnight. */
export function timeFromMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`
}

/**
 * Snaps a click to the slot it landed in.
 *
 * The grid is 15-minute rows and the studio books in 15-minute units (D-46), so
 * a click at 5:37 means the 5:30 slot. Rounding to nearest would make the
 * boundary ambiguous — clicking just below a line would jump forward.
 */
export function snapToSlot(minutes: number, slotMinutes: number): number {
  return Math.floor(minutes / slotMinutes) * slotMinutes
}

export type RoomAvailability = {
  roomId: string
  name: string
  color: string | null
  /** Free for the whole requested span. */
  isFree: boolean
  /** What is in the way, when it is not. */
  conflictLabel: string | null
}

/**
 * Which rooms are free for a span, and what is in the way of the rest.
 *
 * A teacher's question is "I need a room Tuesday at six, what's free?" — so the
 * booking form answers it rather than accepting a guess and refusing afterwards.
 * Both classes and existing bookings count: a room busy with a private is no
 * more available than one busy with a class.
 *
 * Half-open, matching the server, so a room emptying at 6:00 is available at
 * 6:00 rather than reading as occupied.
 *
 * <b>A cancelled class does not hold a room</b> (D-502). The day this reads
 * carries cancelled occurrences unless somebody asks it not to — `HideCancelled`
 * defaults to false and the booking form never passes it — while every server
 * path that decides the same question skips them: `LoadSlotsAsync` takes only
 * `OccurrenceStatus.Scheduled`, so the save allows the booking and
 * `WithARoomFree` recommends the slot (D-450). Two implementations of "is this
 * room free", disagreeing, and the visible half was the wrong one: a
 * recommended time reading *"0 of 6 rooms free then"* with every room struck
 * through, which is testing row #22 surviving its own fix.
 *
 * <b>And a lesson the studio called off does not hold one either</b> (D-779).
 * That is the same disagreement one closure later: a closed day cancels a
 * private's session and leaves its booking standing (D-407), and the server
 * stopped counting it — so the recommended slot came back free while this half
 * went on striking the room through and naming the lesson that is not
 * happening. `bookingIsOff` is the shared rule; reading `status` here is what
 * makes a second one.
 */
export function roomAvailability(
  rooms: {
    roomId: string
    name: string
    color: string | null
    entries: {
      className: string
      startTime: string | null
      endTime: string | null
      /** Optional so a caller with a narrower row still type-checks; absent means live. */
      isCancelled?: boolean
    }[]
  }[],
  bookings: BookedSlot[],
  startMinutes: number,
  endMinutes: number,
  /**
   * The booking being edited, which does not clash with itself (D-396).
   *
   * Reported with a screenshot: opening an existing private showed *"1-Purple
   * Room is taken for this slot"* over a card reading *"Victoria Hogan - Ellie
   * Cotton"* — the very booking on screen. It is telling her the room is taken
   * **by the thing she is editing**, which reads as a real clash and makes the
   * form look broken on the one path where nothing is wrong.
   *
   * Undefined when creating, where every booking is somebody else's.
   */
  editingId?: string,
  /**
   * Approved weekend holds that day (D-710). A room held for somebody is taken
   * for everybody else and free for her - her own privates are what it is for.
   */
  holds: HeldRoom[] = [],
  forStaffId?: string | null,
): RoomAvailability[] {
  return rooms.map((room) => {
    const overlaps = (from: string, to: string) =>
      minutesOf(from) < endMinutes && startMinutes < minutesOf(to)

    const clashingClass = room.entries.find(
      (e) => !e.isCancelled && e.startTime && e.endTime && overlaps(e.startTime, e.endTime),
    )

    const clashingBooking = bookings.find(
      (b) =>
        b.roomId === room.roomId &&
        b.id !== editingId &&
        !bookingIsOff(b) &&
        overlaps(b.startTime, b.endTime),
    )

    const clashingHold = holds.find(
      (h) => h.roomId === room.roomId && h.heldForStaffId !== forStaffId && overlaps(h.startTime, h.endTime),
    )

    const blocker = clashingHold
      ? clashingHold.label
      : clashingClass
      ? clashingClass.className
      : clashingBooking
        ? clashingBooking.isOpen
          ? `Held by ${clashingBooking.staffName}`
          : `${clashingBooking.staffName} - ${clashingBooking.dancerNames.join(', ') || clashingBooking.sizeLabel}`
        : null

    return {
      roomId: room.roomId,
      name: room.name,
      color: room.color,
      isFree: blocker === null,
      conflictLabel: blocker,
    }
  })
}

/**
 * What kind of thing a held room is, as a person reads it.
 *
 * Exported because two screens needed the same words and only one had them:
 * `BookingForm` carried this list for its dropdown while the week grid showed
 * a booking with no indication of what it was, so a private lesson and a
 * rehearsal looked identical. Reported as "why do they look different?" about
 * bookings against classes — half the answer was that a booking never said
 * what it was (D-388).
 */
/**
 * Does this booking answer the filter the reader set (D-770)?
 *
 * **Reported from QA**: the master calendar filtered to one teacher went on
 * showing every private in the building. The classes come from the week
 * request and the server narrows those (`CalendarFilter.Matches`); the
 * privates are overlaid from a second request that the filter never reached
 * (D-372), so a chip saying *Teacher: Hattie Wolford* was telling the truth
 * about half the screen.
 *
 * **A booking answers the questions it can, and fails the ones it cannot.**
 * It has a teacher, a room, a dancer and a kind, so those narrow it. It has no
 * programme and no style — so when the reader asks for Competitive, or for
 * Ballet, a booking is not an answer and is hidden rather than kept. Keeping
 * it would put a private lesson under a heading it does not belong to, which
 * is the same lie in the other direction.
 *
 * Name matching mirrors the server's: `contains`, case-insensitive, because
 * the chip carries what somebody picked from a list of names and a teacher
 * might be listed as *Hattie Wolford* in one place and *Hattie* in another.
 *
 * `hideCancelled`, the scope switch and `privatesOnly` stay with the week
 * view: they are about who is reading, not about what this booking is.
 */
export function bookingMatches(slot: BookedSlot, filter: CalendarFilter): boolean {
  // A booking belongs to no programme and has no style, so it cannot satisfy
  // either and must not be shown as though it had.
  if (filter.program || filter.style) return false

  const has = (haystack: string, needle: string) =>
    haystack.toLowerCase().includes(needle.trim().toLowerCase())

  if (filter.instructor && !has(slot.staffName, filter.instructor)) return false

  // The room is chosen from a list of exact names, as the server matches it.
  if (filter.room && slot.roomName.toLowerCase() !== filter.room.toLowerCase()) return false

  // One dancer's evening is the classes she is in and nothing else (D-606),
  // and a private is exactly that - so a chip naming her must not leave every
  // other family's child on the screen beside her name.
  if (filter.dancer && !slot.dancerIds.includes(filter.dancer)) return false

  if (filter.q?.trim()) {
    // What somebody would actually type: a teacher, a dancer, a room, or what
    // kind of booking it is.
    const haystack = [
      slot.staffName,
      slot.roomName,
      bookingKindLabel(slot.kind),
      ...slot.dancerNames,
    ].join(' ')

    if (!has(haystack, filter.q)) return false
  }

  return true
}

export function bookingKindLabel(kind: BookingKind): string {
  switch (kind) {
    case 'PrivateLesson':
      return 'Private lesson'
    case 'Choreography':
      return 'Choreography'
    case 'Rehearsal':
      return 'Rehearsal'
    default:
      return 'Held room'
  }
}


/**
 * Puts a cancelled lesson back (D-648).
 *
 * The room was freed on purpose when it was cancelled, so this can legitimately
 * fail: the server refuses with the reason when somebody else has taken the
 * slot, or when the lesson repaired a missed class and putting only the booking
 * back would leave the absence open.
 */
export async function reinstateBooking(id: string): Promise<void> {
  const res = await fetch(`/api/bookings/${id}/reinstate`, { method: 'POST' })
  if (res.ok) return

  const body = await res.json().catch(() => null)
  throw new Error(body?.detail ?? body?.title ?? `Could not put it back (${res.status})`)
}
